// Self-upgrade for long-lived SPA tabs.
//
// The dominant cause of "I deployed but the user still sees the old UI" is an
// open tab that loaded the old bundle hours ago and never re-fetched index.html
// (React Router navigates in-memory). HTTP cache headers can't evict that — only
// a real document load can. This polls a tiny `dist/version.json` (served
// `no-cache`) and, when it reports a newer build than the one currently running,
// reloads the tab once onto the fresh bundle.
//
// Safe by construction: it only checks on initial load and when the tab becomes
// visible (the user just refocused — not mid-typing), and a sessionStorage guard
// ensures it reloads at most once per detected build (no reload loops if the new
// bytes somehow don't reach this tab).

declare const __BUILD_ID__: string;

const GUARD_KEY = "bp_reloaded_for_build";

export function startVersionCheck(): void {
  const current = typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "";
  if (!current) return;

  const check = async (): Promise<void> => {
    try {
      const res = await fetch("/version.json", { cache: "no-store" });
      if (!res.ok) return;
      const body = (await res.json()) as { build?: string };
      const latest = body.build;
      if (!latest || latest === current) return;
      // Only attempt a reload once per detected build, so a stuck edge/cache
      // can't trap the tab in a reload loop.
      if (sessionStorage.getItem(GUARD_KEY) === latest) return;
      sessionStorage.setItem(GUARD_KEY, latest);
      window.location.reload();
    } catch {
      /* offline / blocked — ignore, try again next visibility change */
    }
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  // One check shortly after load catches a disk-cached stale shell immediately.
  void check();
}
